import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  MapPin, 
  Calendar, 
  DollarSign,
  Truck,
  User,
  FileText,
  CheckCircle,
  Eye,
  Save,
  X,
  Info,
  XCircle,
  ArrowRight,
  TrendingUp,
  Package,
  Route,
  MoreVertical,
  RefreshCw,
  Download,
  Navigation,
  Building,
  Phone,
  Mail,
  Copy
} from 'lucide-react';
import loadService from '../services/loadService';
import driverService from '../services/driverService';
import vehicleService from '../services/vehicleService';
import { useAuth } from '../contexts/AuthContext';
import { useFinancialRefresh } from '../contexts/FinancialRefreshContext';
import FinancialNotification from '../components/ui/FinancialNotification';

const LoadsPage = () => {
  const [loads, setLoads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingLoad, setEditingLoad] = useState(null);
  const [showNewLoadModal, setShowNewLoadModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [showLoadDetails, setShowLoadDetails] = useState(null);
  const { company, userData } = useAuth();
  const { onLoadCreated, onLoadUpdated, onLoadDeleted } = useFinancialRefresh();
  const [financialNotification, setFinancialNotification] = useState(null);
  const resolvedCompanyId = useMemo(() => {
    if (company?.id) return company.id;
    if (company?.companyId) return company.companyId;
    if (userData?.companyId) return userData.companyId;
    if (userData?.company?.id) return userData.company.id;
    return null;
  }, [company, userData]);
  const activeCompanyId = useMemo(() => {
    if (resolvedCompanyId) {
      return resolvedCompanyId;
    }

    try {
      const stored = localStorage.getItem('company');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.id || parsed?.companyId || null;
      }
    } catch (error) {
      console.warn('Failed to parse stored company data', error);
    }

    return null;
  }, [resolvedCompanyId]);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingLoadId, setDeletingLoadId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [infoModal, setInfoModal] = useState({ open: false, title: '', description: '' });
  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];
  
  const getVehicleLabel = useCallback((vehicle) => {
    if (!vehicle) {
      return '';
    }

    const labelFromNumber = vehicle.vehicleNumber || vehicle.unitNumber;
    if (labelFromNumber) {
      return labelFromNumber;
    }

    if (vehicle.licensePlate) {
      return vehicle.licensePlate;
    }

    const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    if (makeModel) {
      return makeModel;
    }

    return vehicle.id || 'Vehicle';
  }, []);

  // Form state
  const getNextLoadNumber = () => `LD-2024-${String(loads.length + 1).padStart(3, '0')}`;

  const getEmptyFormState = () => ({
    loadNumber: getNextLoadNumber(),
    pickupLocation: '',
    deliveryLocation: '',
    pickupDate: '',
    deliveryDate: '',
    customer: '',
    customerEmail: '',
    customerPhone: '',
    loadType: 'Dry Van',
    weight: '',
    brokerRate: '',
    carrierAmount: '',
    fuelSurcharge: '',
    accessorials: '',
    tollFees: '',
    otherFees: '',
    driverId: '',
    driverName: '',
    vehicleRecordId: '',
    vehicleId: '',
    notes: ''
  });

  const [formData, setFormData] = useState(getEmptyFormState());

  const fetchLoads = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) {
      setLoading(true);
    }

    setLoadError(null);

    try {
      const response = await loadService.listLoads(
        {},
        activeCompanyId ? { companyId: activeCompanyId } : {}
      );
      setLoads(response.loads || []);
    } catch (error) {
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : error?.message || 'Failed to load loads. Please try again.';
      setLoadError(friendlyMessage);
      setLoads([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [activeCompanyId]);

  useEffect(() => {
    fetchLoads();
  }, [fetchLoads]);

  useEffect(() => {
    let isMounted = true;

    const loadDrivers = async () => {
      if (!activeCompanyId) {
        if (isMounted) {
          setDrivers([]);
        }
        return;
      }

      setDriversLoading(true);
      try {
        const response = await driverService.listDrivers({}, { companyId: activeCompanyId });
        if (isMounted) {
          setDrivers(response.drivers || []);
        }
      } catch (error) {
        console.error('Failed to load drivers for loads form:', error);
      } finally {
        if (isMounted) {
          setDriversLoading(false);
        }
      }
    };

    loadDrivers();

    return () => {
      isMounted = false;
    };
  }, [activeCompanyId]);

  useEffect(() => {
    let isMounted = true;

    const loadVehicles = async () => {
      if (!activeCompanyId) {
        if (isMounted) {
          setVehicles([]);
        }
        return;
      }

      setVehiclesLoading(true);
      try {
        const response = await vehicleService.listVehicles({ companyId: activeCompanyId });
        if (isMounted) {
          setVehicles(response.vehicles || []);
        }
      } catch (error) {
        console.error('Failed to load vehicles for loads form:', error);
      } finally {
        if (isMounted) {
          setVehiclesLoading(false);
        }
      }
    };

    loadVehicles();

    return () => {
      isMounted = false;
    };
  }, [activeCompanyId]);

  useEffect(() => {
    if (
      !driversLoading &&
      drivers.length > 0 &&
      !formData.driverId &&
      formData.driverName
    ) {
      const matchedDriver = drivers.find((driver) =>
        driver.name?.toLowerCase() === formData.driverName.toLowerCase()
      );

      if (matchedDriver) {
        setFormData(prev => ({
          ...prev,
          driverId: matchedDriver.id
        }));
      }
    }
  }, [driversLoading, drivers, formData.driverId, formData.driverName]);

  useEffect(() => {
    if (
      !vehiclesLoading &&
      vehicles.length > 0 &&
      !formData.vehicleRecordId &&
      formData.vehicleId
    ) {
      const matchedVehicle = vehicles.find((vehicle) => {
        const label = getVehicleLabel(vehicle);
        return label && label.toLowerCase() === formData.vehicleId.toLowerCase();
      });

      if (matchedVehicle) {
        setFormData(prev => ({
          ...prev,
          vehicleRecordId: matchedVehicle.id
        }));
      }
    }
  }, [vehiclesLoading, vehicles, formData.vehicleRecordId, formData.vehicleId, getVehicleLabel]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'carrierAmount' ? { brokerRate: value } : {})
    }));
  };

  const handleDriverSelect = (event) => {
    const selectedId = event.target.value;
    const selectedDriver = drivers.find((driver) => driver.id === selectedId);

    setFormData(prev => ({
      ...prev,
      driverId: selectedId,
      driverName: selectedDriver?.name || ''
    }));
  };

  const handleVehicleSelect = (event) => {
    const selectedId = event.target.value;
    const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selectedId);

    setFormData(prev => ({
      ...prev,
      vehicleRecordId: selectedId,
      vehicleId: selectedVehicle ? getVehicleLabel(selectedVehicle) : ''
    }));
  };

  const resetForm = () => {
    setFormData(getEmptyFormState());
    setEditingLoad(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeCompanyId) {
      setLoadError('Select a company before managing loads.');
      return;
    }

    setIsSaving(true);
    setLoadError(null);

    const payload = {
      ...formData,
      brokerRate: formData.carrierAmount || '0'
    };

    try {
      let result;
      if (editingLoad?.id) {
        result = await loadService.updateLoad(editingLoad.id, payload, { companyId: activeCompanyId });

        // Calculate financial impact for update
        const revenue = parseCurrencyInput(payload.carrierAmount) + parseCurrencyInput(payload.accessorials);
        const expenses = parseCurrencyInput(payload.fuelSurcharge) + parseCurrencyInput(payload.tollFees) + parseCurrencyInput(payload.otherFees);
        const netRevenue = revenue - expenses;

        // Trigger financial refresh
        onLoadUpdated({ ...result, revenue: netRevenue }, editingLoad.status);

        // Show notification
        setFinancialNotification({
          type: 'load_updated',
          title: 'Load Updated',
          message: `Load ${payload.loadNumber} has been updated successfully`,
          financialImpact: {
            revenue: revenue,
            expense: expenses,
            netImpact: netRevenue
          }
        });
      } else {
        result = await loadService.createLoad(payload, { companyId: activeCompanyId });

        // Calculate financial impact for new load
        const revenue = parseCurrencyInput(payload.carrierAmount) + parseCurrencyInput(payload.accessorials);
        const expenses = parseCurrencyInput(payload.fuelSurcharge) + parseCurrencyInput(payload.tollFees) + parseCurrencyInput(payload.otherFees);
        const netRevenue = revenue - expenses;

        // Trigger financial refresh
        onLoadCreated({ ...result, revenue: netRevenue });

        // Show notification
        setFinancialNotification({
          type: 'load_created',
          title: 'Load Created',
          message: `Load ${payload.loadNumber} has been created successfully`,
          financialImpact: {
            revenue: revenue,
            expense: expenses,
            netImpact: netRevenue
          }
        });
      }

      await fetchLoads({ showLoading: false });
      handleCloseModal();
    } catch (error) {
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : error?.message || 'Failed to save load. Please try again.';
      setLoadError(friendlyMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (load) => {
    setLoadError(null);
    setEditingLoad(load);
    const matchedDriver = drivers.find((driver) =>
      driver.id === load.driverId ||
      (driver.name && load.driverName && driver.name.toLowerCase() === load.driverName.toLowerCase())
    );
    const matchedVehicle = vehicles.find((vehicle) => {
      if (vehicle.id === load.vehicleRecordId) {
        return true;
      }
      if (!load.vehicleId) {
        return false;
      }
      const label = getVehicleLabel(vehicle);
      return label && label.toLowerCase() === load.vehicleId.toLowerCase();
    });

    setFormData({
      loadNumber: load.loadNumber,
      pickupLocation: load.pickupLocation,
      deliveryLocation: load.deliveryLocation,
      pickupDate: load.pickupDate,
      deliveryDate: load.deliveryDate,
      customer: load.customer,
      customerEmail: load.customerEmail || '',
      customerPhone: load.customerPhone || '',
      loadType: load.loadType,
      weight: load.weight.toString(),
      brokerRate: (load.brokerRate ?? load.carrierAmount ?? 0).toString(),
      carrierAmount: load.carrierAmount?.toString() || '',
      fuelSurcharge: load.fuelSurcharge.toString(),
      accessorials: load.accessorials.toString(),
      tollFees: (load.tollFees ?? 0).toString(),
      otherFees: (load.otherFees ?? 0).toString(),
      driverId: load.driverId || matchedDriver?.id || '',
      driverName: load.driverName || matchedDriver?.name || '',
      vehicleRecordId: load.vehicleRecordId || matchedVehicle?.id || '',
      vehicleId: load.vehicleId || (matchedVehicle ? getVehicleLabel(matchedVehicle) : ''),
      notes: load.notes || ''
    });
    setShowNewLoadModal(true);
  };

  const handleDelete = async (loadId) => {
    if (!activeCompanyId) {
      setLoadError('Select a company before deleting loads.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this load?');
    if (!confirmed) {
      return;
    }

    setDeletingLoadId(loadId);
    setLoadError(null);

    try {
      // Find the load to get its financial data before deleting
      const loadToDelete = loads.find(l => l.id === loadId);

      await loadService.deleteLoad(loadId, { companyId: activeCompanyId });

      // Trigger financial refresh
      if (loadToDelete) {
        onLoadDeleted(loadToDelete);

        // Show notification
        setFinancialNotification({
          type: 'load_deleted',
          title: 'Load Deleted',
          message: `Load ${loadToDelete.loadNumber} has been removed from the system`,
          financialImpact: {
            revenue: -(loadToDelete.revenue || 0),
            netImpact: -(loadToDelete.revenue || 0)
          }
        });
      }

      await fetchLoads({ showLoading: false });
    } catch (error) {
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : error?.message || 'Failed to delete load. Please try again.';
      setLoadError(friendlyMessage);
    } finally {
      setDeletingLoadId(null);
    }
  };

  const handleStatusUpdate = async (loadId, status) => {
    if (!status || statusUpdatingId === loadId) {
      return;
    }

    setStatusUpdatingId(loadId);
    setLoadError(null);

    try {
      const updatedLoad = await loadService.updateLoadStatus(
        loadId,
        status,
        activeCompanyId ? { companyId: activeCompanyId } : {}
      );

      setLoads(prev => prev.map(load =>
        load.id === loadId
          ? { ...load, ...updatedLoad }
          : load
      ));

      setShowLoadDetails(prev =>
        prev && prev.id === loadId
          ? { ...prev, ...updatedLoad }
          : prev
      );

      // Trigger financial refresh if status changed to delivered
      if (status === 'delivered') {
        onLoadUpdated(updatedLoad, statusUpdatingId);

        // Show notification for delivery
        setFinancialNotification({
          type: 'load_delivered',
          title: 'Load Delivered',
          message: `Load ${updatedLoad.loadNumber} has been marked as delivered`,
          financialImpact: {
            revenue: updatedLoad.revenue || 0,
            netImpact: updatedLoad.revenue || 0
          }
        });
      }
    } catch (error) {
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : error?.message || 'Failed to update status. Please try again.';
      setLoadError(friendlyMessage);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleNewLoad = () => {
    setLoadError(null);
    resetForm();
    setShowNewLoadModal(true);
  };

  const handleCloseModal = () => {
    setShowNewLoadModal(false);
    resetForm();
  };

  const handleViewDetails = (load) => {
    setShowLoadDetails(load);
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchLoads({ showLoading: false });
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchLoads]);

  const getStatusConfig = (status) => {
    const configs = {
      delivered: {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Delivered'
      },
      in_transit: {
        icon: Navigation,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        label: 'In Transit'
      },
      scheduled: {
        icon: Calendar,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: 'Scheduled'
      },
      cancelled: {
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Cancelled'
      }
    };
    return configs[status] || configs.scheduled;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      high: { color: 'bg-red-500', label: 'High' },
      medium: { color: 'bg-yellow-500', label: 'Medium' },
      low: { color: 'bg-green-500', label: 'Low' }
    };
    return configs[priority] || configs.medium;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const parseCurrencyInput = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const normalized = typeof value === 'string'
      ? value.replace(/,/g, '')
      : value;

    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const openInfoModal = (title, description) => {
    setInfoModal({ open: true, title, description });
  };

  const closeInfoModal = () => {
    setInfoModal(prev => ({ ...prev, open: false }));
  };

  const filteredLoads = loads.filter(load => {
    const matchesSearch = load.loadNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         load.pickupLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         load.deliveryLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         load.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         load.driverName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || load.status === statusFilter;
    
    const now = new Date();
    const loadDate = new Date(load.pickupDate);
    let matchesDate = true;
    
    if (dateFilter === 'today') {
      matchesDate = loadDate.toDateString() === now.toDateString();
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      matchesDate = loadDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      matchesDate = loadDate >= monthAgo;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const stats = {
    totalLoads: filteredLoads.length,
    totalRevenue: filteredLoads.reduce((sum, load) => sum + load.revenue, 0),
    avgRevenue: filteredLoads.length > 0 ? filteredLoads.reduce((sum, load) => sum + load.revenue, 0) / filteredLoads.length : 0,
    deliveredCount: loads.filter(load => load.status === 'delivered').length,
    inTransitCount: loads.filter(load => load.status === 'in_transit').length,
    scheduledCount: loads.filter(load => load.status === 'scheduled').length
  };

  const customerContactValue = showLoadDetails?.customerPhone || showLoadDetails?.customerContact || 'Not provided';
  const detailNetRevenue = showLoadDetails
    ? (Number(showLoadDetails.carrierAmount) || 0)
        + (Number(showLoadDetails.accessorials) || 0)
        - (Number(showLoadDetails.fuelSurcharge) || 0)
        - (Number(showLoadDetails.tollFees) || 0)
        - (Number(showLoadDetails.otherFees) || 0)
    : 0;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <Truck className="w-8 h-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-6 text-xl font-medium text-gray-900">Loading loads...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Enhanced Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6 flex-shrink-0 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Title and Description */}
          <div className="flex-1 hidden md:block">
            <div className="flex items-center gap-3 sm:gap-4 mb-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Package className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Load Management</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Manage and track all your loads efficiently</p>
              </div>
            </div>
            
            {/* Enhanced Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-2 sm:p-3 border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  <div>
                    <p className="text-xs sm:text-sm text-green-700 font-medium">Delivered</p>
                    <p className="text-lg sm:text-xl font-bold text-green-800">{stats.deliveredCount}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-2 sm:p-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <div>
                    <p className="text-xs sm:text-sm text-blue-700 font-medium">In Transit</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-800">{stats.inTransitCount}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-2 sm:p-3 border border-amber-200">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  <div>
                    <p className="text-xs sm:text-sm text-amber-700 font-medium">Scheduled</p>
                    <p className="text-lg sm:text-xl font-bold text-amber-800">{stats.scheduledCount}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-2 sm:p-3 border border-purple-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <div>
                    <p className="text-xs sm:text-sm text-purple-700 font-medium">Total Revenue</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-800">{formatCurrency(stats.totalRevenue)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              title="Export load data"
              className="group flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100">
                <Download className="w-4 h-4" />
              </span>
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              type="button"
              title="Refresh load list"
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="group flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100">
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </span>
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleNewLoad}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2.5 sm:px-6 text-white shadow-lg transition-all hover:-translate-y-0.5 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              <span className="font-semibold text-sm sm:text-base">New Load</span>
            </button>
          </div>
      </div>
    </div>

      {loadError && (
        <div className="px-4 sm:px-6 mt-4">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {loadError}
          </div>
        </div>
      )}

      {/* Enhanced Filters */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
        <div className="flex flex-col gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Search loads, customers, drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
            />
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
            </select>
            
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>

            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all ${
                  viewMode === 'table' 
                    ? 'bg-blue-600 text-white shadow-inner' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all ${
                  viewMode === 'cards' 
                    ? 'bg-blue-600 text-white shadow-inner' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cards
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Table/Cards View */}
      <div className="flex-1 overflow-auto bg-white">
        {viewMode === 'table' ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 border-b border-gray-200 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Load Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Route & Timeline
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Assignment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLoads.map((load, index) => {
                  const statusConfig = getStatusConfig(load.status);
                  const priorityConfig = getPriorityConfig(load.priority);
                  const StatusIcon = statusConfig.icon;
                  const carrierAmount = Number(load.carrierAmount) || 0;
                  const accessorials = Number(load.accessorials) || 0;
                  const fuelCost = Number(load.fuelSurcharge) || 0;
                  const tollFees = Number(load.tollFees) || 0;
                  const otherFees = Number(load.otherFees) || 0;
                  const totalRevenue = carrierAmount + accessorials - fuelCost - tollFees - otherFees;

                  return (
                    <tr 
                      key={load.id} 
                      className={`hover:bg-gradient-to-r hover:from-blue-25 hover:to-blue-50 transition-all duration-200 ${
                        index % 2 === 0 ? 'bg-gray-25' : 'bg-white'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-1 h-12 rounded-full ${priorityConfig.color} shadow-sm`}></div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900">{load.loadNumber}</span>
                              <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${priorityConfig.color} text-white`}>
                                {priorityConfig.label}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-1">{load.customer}</div>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Package className="w-3 h-3" />
                              <span>{load.loadType} • {(load.weight / 1000).toFixed(1)}k lbs</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-gray-900">{load.pickupLocation}</span>
                            <span className="text-xs text-gray-500">({load.pickupDate})</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-400 ml-4">
                            <ArrowRight className="w-3 h-3" />
                            <span>{load.distance} miles</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="font-medium text-gray-900">{load.deliveryLocation}</span>
                            <span className="text-xs text-gray-500">(Due {load.deliveryDate})</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="font-medium text-gray-900">{load.driverName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Truck className="w-3 h-3 text-gray-400" />
                            <span>{load.vehicleId}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-right space-y-1">
                          <div className={`text-lg font-bold ${totalRevenue >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                            {formatCurrency(totalRevenue)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Carrier: {formatCurrency(carrierAmount)}
                          </div>
                          {accessorials !== 0 && (
                            <div className="text-xs text-gray-500">
                              Accessorials: {formatCurrency(accessorials)}
                            </div>
                          )}
                          {fuelCost !== 0 && (
                            <div className="text-xs font-medium text-red-600">
                              Fuel Cost: {formatCurrency(fuelCost)}
                            </div>
                          )}
                          {tollFees !== 0 && (
                            <div className="text-xs font-medium text-red-600">
                              Toll Fees: {formatCurrency(tollFees)}
                            </div>
                          )}
                          {otherFees !== 0 && (
                            <div className="text-xs font-medium text-red-600">
                              Other Fees: {formatCurrency(otherFees)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
                            <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                            <span className={`text-xs font-semibold ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                          </div>
                          <select
                            value={load.status}
                            onChange={(e) => handleStatusUpdate(load.id, e.target.value)}
                            disabled={statusUpdatingId === load.id}
                            className="text-xs px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          >
                            {statusOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewDetails(load)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(load)}
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                            title="Edit Load"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(load.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                            title="Delete Load"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {filteredLoads.map((load) => {
                const statusConfig = getStatusConfig(load.status);
                const priorityConfig = getPriorityConfig(load.priority);
                const StatusIcon = statusConfig.icon;
                const carrierAmount = Number(load.carrierAmount) || 0;
                const accessorials = Number(load.accessorials) || 0;
                const fuelCost = Number(load.fuelSurcharge) || 0;
                const tollFees = Number(load.tollFees) || 0;
                const otherFees = Number(load.otherFees) || 0;
                const totalRevenue = carrierAmount + accessorials - fuelCost - tollFees - otherFees;

                return (
                  <div key={load.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${priorityConfig.color}`}></div>
                        <h3 className="font-semibold text-gray-900">{load.loadNumber}</h3>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </div>
                        <select
                          value={load.status}
                          onChange={(e) => handleStatusUpdate(load.id, e.target.value)}
                          disabled={statusUpdatingId === load.id}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                      {/* Route */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-gray-900">{load.pickupLocation}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-6">
                          <ArrowRight className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-600">{load.deliveryLocation}</span>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Pickup</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(load.pickupDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Delivery</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(load.deliveryDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Assignment */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Driver</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{load.driverName}</p>
                        <p className="text-xs text-gray-600">{load.vehicleId}</p>
                      </div>

                      {/* Revenue */}
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Net Revenue</span>
                          <span className={`text-base font-bold ${totalRevenue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(totalRevenue)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Carrier Amount</span>
                          <span className="text-green-600 font-medium">{formatCurrency(carrierAmount)}</span>
                        </div>
                        {accessorials !== 0 && (
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Accessorials</span>
                            <span>{formatCurrency(accessorials)}</span>
                          </div>
                        )}
                        {fuelCost !== 0 && (
                          <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                            <span>Fuel Cost</span>
                            <span>-{formatCurrency(fuelCost)}</span>
                          </div>
                        )}
                        {tollFees !== 0 && (
                          <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                            <span>Toll Fees</span>
                            <span>-{formatCurrency(tollFees)}</span>
                          </div>
                        )}
                        {otherFees !== 0 && (
                          <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                            <span>Other Fees</span>
                            <span>-{formatCurrency(otherFees)}</span>
                          </div>
                        )}
                        {tollFees !== 0 && (
                          <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                            <span>Toll Fees</span>
                            <span>-{formatCurrency(tollFees)}</span>
                          </div>
                        )}
                        {otherFees !== 0 && (
                          <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                            <span>Other Fees</span>
                            <span>-{formatCurrency(otherFees)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
                      <button
                        onClick={() => setShowLoadDetails(load)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingLoad(load)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(load.id)}
                          disabled={deletingLoadId === load.id}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label="Delete load"
                        >
                          <Trash2 className={`w-4 h-4 ${deletingLoadId === load.id ? 'animate-pulse' : ''}`} />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredLoads.map((load) => {
                const statusConfig = getStatusConfig(load.status);
                const priorityConfig = getPriorityConfig(load.priority);
                const StatusIcon = statusConfig.icon;
                const carrierAmount = Number(load.carrierAmount) || 0;
                const accessorials = Number(load.accessorials) || 0;
                const fuelCost = Number(load.fuelSurcharge) || 0;
                const tollFees = Number(load.tollFees) || 0;
                const otherFees = Number(load.otherFees) || 0;
                const totalRevenue = carrierAmount + accessorials - fuelCost - tollFees - otherFees;

              return (
                <div key={load.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-1.5 h-16 rounded-full ${priorityConfig.color}`}></div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl font-bold text-gray-900">{load.loadNumber}</span>
                          <span className={`px-2 py-1 text-xs font-bold rounded-full ${priorityConfig.color} text-white`}>
                            {priorityConfig.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{load.customer}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
                        <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                        <span className={`text-sm font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <select
                        value={load.status}
                        onChange={(e) => handleStatusUpdate(load.id, e.target.value)}
                        disabled={statusUpdatingId === load.id}
                        className="text-xs px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-green-500" />
                        <span className="font-medium">{load.pickupLocation}</span>
                      </div>
                      <div className="text-xs text-gray-500">{load.pickupDate}</div>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500 mx-2">{load.distance} miles</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-red-500" />
                        <span className="font-medium">{load.deliveryLocation}</span>
                      </div>
                      <div className="text-xs text-gray-500">{load.deliveryDate}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{load.driverName}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Truck className="w-4 h-4" />
                        <span>{load.vehicleId}</span>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className={`text-xl font-bold ${totalRevenue >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                        {formatCurrency(totalRevenue)}
                      </div>
                      <div className="text-xs text-gray-500">Net Revenue</div>
                      <div className="text-xs text-green-600 font-medium">Carrier: {formatCurrency(carrierAmount)}</div>
                      {accessorials !== 0 && (
                        <div className="text-xs text-gray-500">Accessorials: {formatCurrency(accessorials)}</div>
                      )}
                      {fuelCost !== 0 && (
                        <div className="text-xs text-red-600 font-medium">Fuel Cost: {formatCurrency(fuelCost)}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Package className="w-3 h-3" />
                      {load.loadType} • {(load.weight / 1000).toFixed(1)}k lbs
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(load)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(load)}
                        className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Edit Load"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(load.id)}
                        disabled={deletingLoadId === load.id}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                        title="Delete Load"
                        aria-label="Delete load"
                      >
                        <Trash2 className={`w-4 h-4 ${deletingLoadId === load.id ? 'animate-pulse' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {filteredLoads.length === 0 && (
          <div className="text-center py-20">
            <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <FileText className="w-16 h-16 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No loads found</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your filters to see more results, or create a new load to get started.'
                : 'Get started by creating your first load to begin tracking your shipments.'
              }
            </p>
            <button
              onClick={handleNewLoad}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              <span className="font-semibold">Create Your First Load</span>
            </button>
          </div>
        )}
      </div>

      {/* Load Details Modal */}
      {showLoadDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold">{showLoadDetails.loadNumber}</h2>
                    <p className="text-blue-100 text-sm sm:text-base">{showLoadDetails.customer}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLoadDetails(null)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Route Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Route className="w-5 h-5 text-blue-600" />
                    Route Details
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">Pickup</span>
                      </div>
                      <p className="text-sm text-gray-600 ml-5">{showLoadDetails.pickupLocation}</p>
                      <p className="text-xs text-gray-500 ml-5">{showLoadDetails.pickupAddress}</p>
                      <p className="text-xs text-gray-500 ml-5">{showLoadDetails.pickupDate}</p>
                    </div>
                    <div className="border-l-2 border-gray-300 h-4 ml-1.5"></div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">Delivery</span>
                      </div>
                      <p className="text-sm text-gray-600 ml-5">{showLoadDetails.deliveryLocation}</p>
                      <p className="text-xs text-gray-500 ml-5">{showLoadDetails.deliveryAddress}</p>
                      <p className="text-xs text-gray-500 ml-5">{showLoadDetails.deliveryDate}</p>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        <strong>Distance:</strong> {showLoadDetails.distance} miles
                      </p>
                    </div>
                  </div>
                </div>

                {/* Load Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Load Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Load Type</p>
                        <p className="font-medium text-gray-900">{showLoadDetails.loadType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Weight</p>
                        <p className="font-medium text-gray-900">{(showLoadDetails.weight / 1000).toFixed(1)}k lbs</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status</p>
                      <div className="flex items-center gap-3">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusConfig(showLoadDetails.status).bgColor} ${getStatusConfig(showLoadDetails.status).borderColor}`}>
                          {React.createElement(getStatusConfig(showLoadDetails.status).icon, {
                            className: `w-4 h-4 ${getStatusConfig(showLoadDetails.status).color}`
                          })}
                          <span className={`font-medium text-sm ${getStatusConfig(showLoadDetails.status).color}`}>
                            {getStatusConfig(showLoadDetails.status).label}
                          </span>
                        </div>
                        <select
                          value={showLoadDetails.status}
                          onChange={(e) => handleStatusUpdate(showLoadDetails.id, e.target.value)}
                          disabled={statusUpdatingId === showLoadDetails.id}
                          className="text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Building className="w-5 h-5 text-blue-600" />
                    Customer Details
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="font-medium text-gray-900 text-lg">{showLoadDetails.customer}</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{customerContactValue}</span>
                        <button
                          onClick={() => copyToClipboard(showLoadDetails.customerPhone || showLoadDetails.customerContact || '')}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          disabled={!showLoadDetails.customerPhone && !showLoadDetails.customerContact}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{showLoadDetails.customerEmail}</span>
                        <button
                          onClick={() => copyToClipboard(showLoadDetails.customerEmail)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assignment Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Assignment
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Driver</p>
                      <p className="font-medium text-gray-900">{showLoadDetails.driverName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Vehicle</p>
                      <p className="font-medium text-gray-900">{showLoadDetails.vehicleId}</p>
                    </div>
                  </div>
                </div>

                {/* Created By Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Created By
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">User</p>
                      <p className="font-medium text-gray-900">{showLoadDetails.createdBy}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Role</p>
                      <p className="font-medium text-gray-900">{showLoadDetails.createdByRole}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Created Date</p>
                      <p className="font-medium text-gray-900">{new Date(showLoadDetails.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div className="lg:col-span-2 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    Financial Breakdown
                  </h3>
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Carrier Amount</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(showLoadDetails.carrierAmount)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Accessorials</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(showLoadDetails.accessorials)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Fuel Cost</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(showLoadDetails.fuelSurcharge)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Toll Fees</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(showLoadDetails.tollFees || 0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Other Fees</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(showLoadDetails.otherFees || 0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Net Revenue</p>
                        <p className={`text-2xl font-bold ${detailNetRevenue >= 0 ? 'text-green-800' : 'text-red-600'}`}>
                          {formatCurrency(detailNetRevenue)}
                        </p>
                      </div>
                    </div>
                    </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowLoadDetails(null)}
                  className="w-full sm:w-auto px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors order-2 sm:order-1"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleEdit(showLoadDetails);
                    setShowLoadDetails(null);
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Load
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Load Modal */}
      {showNewLoadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg">
                    {editingLoad ? <Edit3 className="w-5 h-5 sm:w-6 sm:h-6" /> : <Plus className="w-5 h-5 sm:w-6 sm:h-6" />}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold">
                      {editingLoad ? 'Edit Load' : 'Create New Load'}
                    </h2>
                    <p className="text-blue-100 text-sm sm:text-base hidden sm:block">
                      {editingLoad ? 'Update load information' : 'Add a new load to your fleet'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-200px)]">
              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                  {/* Route Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-200 pb-2">
                      <Route className="w-5 h-5 text-blue-600" />
                      Route Information
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Pickup Location *
                        </label>
                        <input
                          type="text"
                          name="pickupLocation"
                          value={formData.pickupLocation}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="e.g., Chicago, IL"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Delivery Location *
                        </label>
                        <input
                          type="text"
                          name="deliveryLocation"
                          value={formData.deliveryLocation}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="e.g., Atlanta, GA"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Pickup Date *
                          </label>
                          <input
                            type="date"
                            name="pickupDate"
                            value={formData.pickupDate}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Delivery Date *
                          </label>
                          <input
                            type="date"
                            name="deliveryDate"
                            value={formData.deliveryDate}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Load Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-200 pb-2">
                      <Package className="w-5 h-5 text-blue-600" />
                      Load Details
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Load Number *
                        </label>
                        <input
                          type="text"
                          name="loadNumber"
                          value={formData.loadNumber}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="LD-2024-010"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Customer *
                        </label>
                        <input
                          type="text"
                          name="customer"
                          value={formData.customer}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="Customer name"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Customer Email *
                          </label>
                          <input
                            type="email"
                            name="customerEmail"
                            value={formData.customerEmail}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="customer@company.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Customer Phone
                          </label>
                          <input
                            type="tel"
                            name="customerPhone"
                            value={formData.customerPhone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Load Type *
                          </label>
                          <select
                            name="loadType"
                            value={formData.loadType}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          >
                            <option value="Dry Van">Dry Van</option>
                            <option value="Refrigerated">Refrigerated</option>
                            <option value="Flatbed">Flatbed</option>
                            <option value="Step Deck">Step Deck</option>
                            <option value="Container">Container</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Weight (lbs)
                          </label>
                          <input
                            type="number"
                            name="weight"
                            value={formData.weight}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="e.g., 45000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-200 pb-2">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                      Financial Details
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                          Carrier Amount * ($)
                          <button
                            type="button"
                            onClick={() => openInfoModal('Carrier Amount', 'Total amount your company collects for carrying the load, including your margin.')}
                            className="text-gray-400 hover:text-blue-600"
                            aria-label="Carrier amount info"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </label>
                        <input
                          type="number"
                          name="carrierAmount"
                          value={formData.carrierAmount}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="e.g., 2200"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                            Fuel ($)
                            <button
                              type="button"
                              onClick={() => openInfoModal('Fuel', 'Fuel charges collected from the broker to cover diesel costs for this load.')}
                              className="text-gray-400 hover:text-blue-600"
                              aria-label="Fuel info"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </label>
                          <input
                            type="number"
                            name="fuelSurcharge"
                            value={formData.fuelSurcharge}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="e.g., 150"
                          />
                        </div>

                        <div>
                          <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                            Accessorials ($)
                            <button
                              type="button"
                              onClick={() => openInfoModal('Accessorials', 'Extra service fees such as detention, lumper, layover, or other special handling charges.')}
                              className="text-gray-400 hover:text-blue-600"
                              aria-label="Accessorials info"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </label>
                          <input
                            type="number"
                            name="accessorials"
                            value={formData.accessorials}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="e.g., 100"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                            Toll Fees ($)
                            <button
                              type="button"
                              onClick={() => openInfoModal('Toll Fees', 'Total toll expenses paid while carrying this load.')}
                              className="text-gray-400 hover:text-blue-600"
                              aria-label="Toll fees info"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </label>
                          <input
                            type="number"
                            name="tollFees"
                            value={formData.tollFees}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="e.g., 75"
                          />
                        </div>

                        <div>
                          <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                            Other Fees ($)
                            <button
                              type="button"
                              onClick={() => openInfoModal('Other Fees', 'Any additional costs such as permits, escorts, or miscellaneous expenses.')}
                              className="text-gray-400 hover:text-blue-600"
                              aria-label="Other fees info"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </label>
                          <input
                            type="number"
                            name="otherFees"
                            value={formData.otherFees}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="e.g., 50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assignment */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-200 pb-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Assignment
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Driver Name *
                        </label>
                        <select
                          name="driverId"
                          value={formData.driverId}
                          onChange={handleDriverSelect}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                        >
                          <option value="">
                            {driversLoading ? 'Loading drivers...' : 'Select a driver'}
                          </option>
                          {drivers.map((driver) => (
                            <option key={driver.id} value={driver.id}>
                              {driver.name}
                            </option>
                          ))}
                        </select>
                        {!driversLoading && drivers.length === 0 && (
                          <p className="text-xs text-amber-600 mt-2">
                            No drivers available. Add drivers to assign loads.
                          </p>
                        )}
                        {formData.driverName && !formData.driverId && (
                          <p className="text-xs text-gray-500 mt-2">
                            Current assignment: {formData.driverName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Vehicle ID *
                        </label>
                        <select
                          name="vehicleRecordId"
                          value={formData.vehicleRecordId || ''}
                          onChange={handleVehicleSelect}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                        >
                          <option value="">
                            {vehiclesLoading ? 'Loading vehicles...' : 'Select a vehicle'}
                          </option>
                          {vehicles.map((vehicle) => (
                            <option key={vehicle.id} value={vehicle.id}>
                              {getVehicleLabel(vehicle)}
                            </option>
                          ))}
                        </select>
                        {!vehiclesLoading && vehicles.length === 0 && (
                          <p className="text-xs text-amber-600 mt-2">
                            No vehicles available. Add a vehicle before assigning loads.
                          </p>
                        )}
                        {formData.vehicleId && !formData.vehicleRecordId && (
                          <p className="text-xs text-gray-500 mt-2">
                            Current vehicle reference: {formData.vehicleId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-200 pb-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Additional Notes
                  </h3>
                  <div>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Additional notes or special instructions..."
                    />
                  </div>
                </div>

                {/* Revenue Summary */}
                {(formData.carrierAmount || formData.fuelSurcharge || formData.accessorials || formData.tollFees || formData.otherFees) && (
                  (() => {
                    const carrierAmountValue = parseCurrencyInput(formData.carrierAmount);
                    const accessorialsValue = parseCurrencyInput(formData.accessorials);
                    const fuelCostValue = parseCurrencyInput(formData.fuelSurcharge);
                    const tollFeesValue = parseCurrencyInput(formData.tollFees);
                    const otherFeesValue = parseCurrencyInput(formData.otherFees);
                    const totalRevenue = carrierAmountValue + accessorialsValue - fuelCostValue - tollFeesValue - otherFeesValue;

                    return (
                      <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-100 rounded-xl p-6 border border-green-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                            <span className="text-lg font-bold text-green-800">Revenue Breakdown</span>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-green-600 font-semibold">Net Revenue</p>
                            <p className={`text-3xl font-bold ${totalRevenue >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {formatCurrency(totalRevenue)}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm text-green-700">
                          <div>
                            <span className="font-medium">Carrier Amount</span>
                            <div className="font-bold">{formatCurrency(carrierAmountValue)}</div>
                          </div>
                          <div>
                            <span className="font-medium">Accessorials</span>
                            <div className="font-bold">{formatCurrency(accessorialsValue)}</div>
                          </div>
                          <div>
                            <span className="font-medium">Fuel Cost</span>
                            <div className="font-bold text-red-600">{formatCurrency(fuelCostValue)}</div>
                          </div>
                          <div>
                            <span className="font-medium">Toll Fees</span>
                            <div className="font-bold text-red-600">{formatCurrency(tollFeesValue)}</div>
                          </div>
                          <div>
                            <span className="font-medium">Other Fees</span>
                            <div className="font-bold text-red-600">{formatCurrency(otherFeesValue)}</div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-green-200 flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-600">Net Revenue</span>
                          <span className={`text-2xl font-bold ${totalRevenue >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                            {formatCurrency(totalRevenue)}
                          </span>
                        </div>
                      </div>
                    );
                  })()
                )}

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold transition-colors order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl order-1 sm:order-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{editingLoad ? 'Update Load' : 'Create Load'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {infoModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="text-lg font-semibold text-gray-900">{infoModal.title}</h4>
              </div>
              <button
                onClick={closeInfoModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close info dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{infoModal.description}</p>
            <div className="flex justify-end">
              <button
                onClick={closeInfoModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Notification */}
      <FinancialNotification
        notification={financialNotification}
        onClose={() => setFinancialNotification(null)}
      />
    </div>
  );
};

export default LoadsPage;
